import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Loader2, Mail, RefreshCw } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuthContext } from '../context/AuthContext';
import { AuthShell, AuthButton, AuthAlert, authInputClass, authLabelClass } from '../components/AuthShell';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [verificationState, setVerificationState] = useState<'loading' | 'success' | 'error' | 'no-token'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  const { verifyEmail, resendVerification } = useAuthContext();

  useEffect(() => {
    if (!token) {
      setVerificationState('no-token');
      return;
    }
    const handleVerifyEmail = async () => {
      try {
        const result = await verifyEmail({ token });
        if (result.success) setVerificationState('success');
        else {
          setVerificationState('error');
          setErrorMessage(result.error || 'Verification failed');
        }
      } catch (error: any) {
        setVerificationState('error');
        setErrorMessage(error.message || 'Verification failed');
      }
    };
    handleVerifyEmail();
  }, [token, verifyEmail]);

  const handleResendVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail) {
      setResendMessage('Please enter your email address');
      return;
    }
    setResendLoading(true);
    setResendMessage(null);
    try {
      const result = await resendVerification(resendEmail);
      setResendMessage(result.success ? 'Verification email sent! Please check your inbox.' : result.error || 'Failed to send verification email');
    } catch (error: any) {
      setResendMessage(error.message || 'Failed to send verification email');
    } finally {
      setResendLoading(false);
    }
  };

  const resendForm = (label: string) => (
    <form onSubmit={handleResendVerification} className="space-y-4">
      <div className="space-y-1.5">
        <label htmlFor="resend-email" className={authLabelClass}>Email address</label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-inkblack-60" />
          <input id="resend-email" name="resend-email" type="email" required value={resendEmail} onChange={(e) => setResendEmail(e.target.value)} className={authInputClass} placeholder="you@professional.com" />
        </div>
      </div>
      <AuthButton type="submit" loading={resendLoading}>
        {resendLoading ? 'Sending…' : (<><RefreshCw className="h-4 w-4" /> {label}</>)}
      </AuthButton>
      {resendMessage && (
        <AuthAlert tone={resendMessage.includes('sent') ? 'success' : 'error'} title={resendMessage.includes('sent') ? 'Email sent' : 'Couldn’t send'}>
          {resendMessage}
        </AuthAlert>
      )}
    </form>
  );

  const renderContent = () => {
    switch (verificationState) {
      case 'loading':
        return (
          <div className="space-y-3 text-center">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-inkblack-80" />
            <h3 className="text-lg font-semibold text-inkblack">Verifying your email…</h3>
            <p className="text-sm text-inkblack-60">Please wait while we confirm your email address.</p>
          </div>
        );
      case 'success':
        return (
          <div className="space-y-5">
            <AuthAlert tone="success" title="Email verified">
              Your email has been verified. You can now sign in to your account.
            </AuthAlert>
            <Link to="/auth/login" className="inline-flex w-full items-center justify-center rounded-xl bg-inkblack px-4 py-2.5 text-sm font-semibold text-white no-underline shadow-sm transition hover:-translate-y-0.5">
              Continue to sign in
            </Link>
          </div>
        );
      case 'error':
        return (
          <div className="space-y-5">
            <AuthAlert title="Verification failed">{errorMessage || 'We could not verify your email address.'}</AuthAlert>
            <p className="text-sm text-inkblack-60">Enter your email and we&rsquo;ll send a new verification link.</p>
            {resendForm('Resend verification email')}
          </div>
        );
      case 'no-token':
        return (
          <div className="space-y-5">
            <AuthAlert title="Invalid verification link">This link is invalid or missing its token.</AuthAlert>
            <p className="text-sm text-inkblack-60">Need a new link? Enter your email below.</p>
            {resendForm('Send verification email')}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <SEO title="Verify Email" description="Verify your email address to complete registration" keywords="email verification" />

      <AuthShell
        title="Email verification"
        subtitle="Confirm your email address to activate your account."
        footer={
          <>
            Already verified?{' '}
            <Link to="/auth/login" className="font-semibold text-inkblack no-underline hover:text-inkblack">
              Sign in
            </Link>
          </>
        }
      >
        {renderContent()}
      </AuthShell>
    </>
  );
};

export default VerifyEmailPage;
